import './App.css';

const isPrivacyPage = window.location.pathname.endsWith('/privacy-policy.html');

const features = [
  {
    iconClass: 'card__icon card__icon--alphabet',
    icon: 'A-Z',
    title: 'Alphabet Discovery',
    body: 'Children explore letters through bright visuals, clear pronunciations, and playful repetition designed for early learners.',
  },
  {
    iconClass: 'card__icon card__icon--animals',
    icon: 'AN',
    title: 'Animal Connections',
    body: 'Every letter pairs with familiar animals so children connect sounds, names, and pictures in a memorable way.',
  },
  {
    iconClass: 'card__icon card__icon--trace',
    icon: 'TR',
    title: 'Letter Tracing',
    body: 'Tracing activities help build hand control, shape recognition, and confidence while learning how letters are formed.',
  },
  {
    iconClass: 'card__icon card__icon--find',
    icon: 'FD',
    title: 'Find & Match Games',
    body: 'Kids practice spotting the correct letter with focused mini-games that reinforce recognition and listening.',
  },
  {
    iconClass: 'card__icon card__icon--drive',
    icon: 'GO',
    title: 'Drive the Letter',
    body: 'Motion-based play turns learning into an adventure and keeps children engaged longer than static flashcards.',
  },
  {
    iconClass: 'card__icon card__icon--listen',
    icon: 'AU',
    title: 'Audio Learning',
    body: 'Pronunciation prompts, background sounds, and spoken cues support listening skills alongside visual learning.',
  },
];

function LandingPage() {
  return (
    <div className="app-shell">
      <section className="hero">
        <div className="hero__nav">
          <a className="brand" href="/">
            <img className="brand__mark" src="/logo192.png" alt="ABC Wonderland" />
            <div>
              <p className="brand__name">ABC Wonderland</p>
              <p className="brand__tag">Early Learning Adventure</p>
            </div>
          </a>
          <div className="nav-links">
            <a className="nav-link" href="#features">
              Features
            </a>
            <a className="nav-link" href="#about">
              About
            </a>
            <a className="button--ghost" href="/privacy-policy.html">
              Privacy Policy
            </a>
          </div>
        </div>

        <div className="hero__content">
          <div className="hero-copy">
            <p className="hero-copy__eyebrow">Interactive Alphabet App</p>
            <h1>
              A playful path into the <span>world of letters</span>
            </h1>
            <p>
              ABC Wonderland is a kid-friendly learning app built around alphabet
              discovery, animals, sounds, tracing, and mini-games. It turns early
              literacy into a colorful adventure children can enjoy on their own or
              with a parent.
            </p>
            <div className="hero-actions">
              <a className="button" href="#features">
                Explore Features
              </a>
              <a className="button--ghost" href="/privacy-policy.html">
                Open Privacy Policy
              </a>
            </div>
            <div className="hero-points">
              <div className="hero-point">
                <strong>Letters A-Z</strong>
                <span>Designed for preschool and early primary learners.</span>
              </div>
              <div className="hero-point">
                <strong>Safe & Friendly</strong>
                <span>Simple interactions that keep the focus on learning.</span>
              </div>
              <div className="hero-point">
                <strong>Audio + Visual</strong>
                <span>Animals, colors, motion, and sound working together.</span>
              </div>
            </div>
          </div>

          <div className="hero-card">
            <div className="hero-card__badge">ABC Wonderland App</div>
            <img
              className="hero-card__image"
              src="/logo512.png"
              alt="ABC Wonderland app artwork"
            />
            <p className="hero-card__caption">
              A bright, playful app experience for alphabet recognition, tracing,
              listening, and letter-based games.
            </p>
          </div>
        </div>
      </section>

      <section className="section" id="features">
        <div className="section-heading">
          <p>What children can do</p>
          <h2>Built around the activities inside the Flutter app</h2>
        </div>
        <div className="feature-grid">
          {features.map((feature) => (
            <article className="card" key={feature.title}>
              <div className={feature.iconClass}>{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section" id="about">
        <div className="section-heading">
          <p>Why it works</p>
          <h2>Simple, visual, repeatable learning for young children</h2>
        </div>
        <div className="info-grid">
          <article className="card card--warm">
            <h3>Made for early learners</h3>
            <p>
              ABC Wonderland focuses on clear interactions, playful repetition, and
              familiar themes like animals and motion. That keeps attention high
              while reinforcing core alphabet skills.
            </p>
            <div className="pill-list">
              <span className="pill">Alphabet tracing</span>
              <span className="pill">Animal sounds</span>
              <span className="pill">Recognition games</span>
              <span className="pill">Listening practice</span>
            </div>
          </article>

          <article className="card">
            <h3>Inside the app</h3>
            <p>
              The current experience includes alphabet learning, trace-the-letter,
              spell-the-animal, find-the-letter, pick-the-animal, and drive-the-letter
              activities.
            </p>
            <ol className="steps">
              <li>
                <span>1</span>
                <p>See the letter, animal, and sound together.</p>
              </li>
              <li>
                <span>2</span>
                <p>Practice tracing and matching through guided play.</p>
              </li>
              <li>
                <span>3</span>
                <p>Repeat through mini-games that strengthen memory and recognition.</p>
              </li>
            </ol>
          </article>
        </div>
      </section>

      <footer className="footer">
        <span>ABC Wonderland</span>
        <span>
          Privacy policy: <a href="/privacy-policy.html">/privacy-policy.html</a>
        </span>
      </footer>
    </div>
  );
}

function PrivacyPolicyPage() {
  return (
    <div className="privacy-shell">
      <div className="privacy-card">
        <a className="brand" href="/">
          <img className="brand__mark" src="/logo192.png" alt="ABC Wonderland" />
          <div>
            <p className="brand__name">ABC Wonderland</p>
            <p className="brand__tag">Privacy Policy</p>
          </div>
        </a>

        <h1>Privacy Policy</h1>
        <p className="privacy-meta">Last updated: March 9, 2026</p>

        <div className="privacy-copy">
          <h2>Overview</h2>
          <p>
            ABC Wonderland is an educational app for children. This page explains
            what information may be handled when people use the app or this website.
          </p>

          <h2>Information We Collect</h2>
          <p>
            The app is designed to be simple and child-friendly. We do not request
            account registration, and we do not intentionally collect personal
            information from children through the learning activities themselves.
          </p>

          <h2>How Information Is Used</h2>
          <p>
            Information, if any is collected by the platform or hosting provider, is
            used only to operate, maintain, and improve the app and website
            experience.
          </p>

          <h2>Children&apos;s Privacy</h2>
          <p>
            ABC Wonderland is intended for early learners and should be used with
            parental awareness where appropriate. We do not knowingly collect
            personal information from children.
          </p>

          <h2>Third-Party Services</h2>
          <p>
            App stores, hosting platforms, or analytics tools may have their own
            privacy practices. Users should review those services separately if they
            are used in distribution or deployment.
          </p>

          <h2>Contact</h2>
          <p>
            If you want a contact email added here before deployment, replace this
            section with your official support email or business contact details.
          </p>

          <h2>Summary</h2>
          <ul>
            <li>ABC Wonderland is built as a child-focused learning experience.</li>
            <li>We do not intentionally collect personal information from children.</li>
            <li>This privacy policy can be updated as the app evolves.</li>
          </ul>
        </div>

        <div className="privacy-actions">
          <a className="button" href="/">
            Back to Landing Page
          </a>
        </div>
      </div>
    </div>
  );
}

function App() {
  return isPrivacyPage ? <PrivacyPolicyPage /> : <LandingPage />;
}

export default App;
